/// <reference path="../../nativewind-augmentation.d.ts" />
// Copyright (c) Ping. All rights reserved.
//
// In-chat Dice Roll tile. Each player rolls their own die (own client
// generates 1–6); higher value wins. Both clients animate + agree.

import { Dices } from 'lucide-react-native';
import { useEffect, useRef } from 'react';
import { Animated, Easing, View, Text } from 'react-native';
import { useColorScheme } from '../../foundations/colorScheme';
import { tokens } from '../../design-system/tokens';
import { useTileMaxWidth } from '../tileConstraint';
import { Banner, type GameColors, Header, PrimaryButton, WaitingRow } from './CoinFlipTile';
import { encodeDiceRoll, hasMoved, randomDie, readDiceState } from './gameLogic';
import type { GameSession } from '../../sync/useGameSessions';

export interface DiceRollTileProps {
  readonly session: GameSession;
  readonly isCreator: boolean;
  readonly onBrand?: boolean | undefined;
  readonly onMove: (moveIdx: number, payload: string) => void;
  readonly selfName?: string | undefined;
  readonly opponentName?: string | undefined;
}

export function DiceRollTile({
  session,
  isCreator,
  onBrand,
  onMove,
  selfName,
  opponentName,
}: DiceRollTileProps): JSX.Element {
  const scheme = useColorScheme();
  const c: GameColors = tokens.color.semantic[scheme === 'dark' ? 'dark' : 'light'];
  // Thread side panel: cap the tile to the provided width.
  const tileMax = useTileMaxWidth();
  const accent = onBrand ? '#FFFFFF' : c.accent.indigo;
  const subText = onBrand ? 'rgba(255,255,255,0.75)' : c.text.tertiary;
  const state = readDiceState(session);

  const mySeatIdx: 0 | 1 = isCreator ? 0 : 1;
  const iRolled = hasMoved(session, mySeatIdx);
  const myValue = isCreator ? state.creatorValue : state.opponentValue;
  const theirValue = isCreator ? state.opponentValue : state.creatorValue;

  const roll = (): void => onMove(mySeatIdx, encodeDiceRoll(randomDie()));

  const iWon = state.resolved && state.winnerSeat === mySeatIdx;
  const winnerName =
    state.winnerSeat === mySeatIdx ? selfName ?? 'You' : opponentName ?? 'They';

  return (
    <View
      className={`w-[280px] max-w-full rounded-[18px] p-md gap-sm border ${
        onBrand
          ? 'bg-light-accent-brand dark:bg-[#537EFF] border-white/10'
          : 'bg-light-surface-secondary dark:bg-[#1A1D24] border-light-border-secondary dark:border-white/[0.06]'
      }`}
      style={tileMax !== null ? { maxWidth: tileMax } : undefined}
      accessibilityLabel="Dice roll game"
    >
      <Header title="Dice Roll" Icon={Dices} colorAccent={accent} />

      <View className="flex-row items-center justify-around py-sm">
        <DieColumn
          label={selfName ?? 'You'}
          value={myValue}
          highlight={state.resolved && state.winnerSeat === mySeatIdx}
        />
        <Text className="font-sans font-bold text-[13px] text-light-text-tertiary dark:text-[#B5BAC3]">
          vs
        </Text>
        <DieColumn
          label={opponentName ?? 'Them'}
          value={theirValue ?? null}
          highlight={state.resolved && state.winnerSeat === (isCreator ? 1 : 0)}
        />
      </View>

      {state.resolved ? (
        <Banner
          text={state.winnerSeat === -1 ? "It's a draw!" : `${winnerName} won!`}
          win={iWon}
          colors={c}
        />
      ) : !iRolled ? (
        <PrimaryButton
          label="Roll the dice!"
          onPress={roll}
          accent={accent}
          {...(onBrand ? { textColor: c.accent.indigo } : {})}
        />
      ) : (
        <WaitingRow text="Waiting for opponent to roll…" color={subText} />
      )}
    </View>
  );
}

function DieColumn({
  label,
  value,
  highlight,
}: {
  label: string;
  value: number | null;
  highlight: boolean;
}): JSX.Element {
  return (
    <View className="items-center gap-1">
      <Die value={value} highlight={highlight} />
      <Text
        numberOfLines={1}
        className="font-sans text-[11px] leading-4 max-w-[80px] text-light-text-secondary dark:text-[#B5BAC3]"
      >
        {label}
      </Text>
    </View>
  );
}

function Die({ value, highlight }: { value: number | null; highlight: boolean }): JSX.Element {
  // Tumble animation when a value lands.
  const spin = useRef(new Animated.Value(0)).current;
  const spunFor = useRef<number | null>(null);
  useEffect(() => {
    if (value !== null && spunFor.current !== value) {
      spunFor.current = value;
      spin.setValue(0);
      Animated.timing(spin, {
        toValue: 1,
        duration: 700,
        easing: Easing.out(Easing.cubic),
        useNativeDriver: true,
      }).start();
    }
  }, [value, spin]);
  const rotate = spin.interpolate({ inputRange: [0, 1], outputRange: ['0deg', '720deg'] });

  return (
    <Animated.View
      style={{
        width: 56,
        height: 56,
        borderRadius: 12,
        backgroundColor: highlight ? '#3CCB4F' : '#FFFFFF',
        borderWidth: 2,
        borderColor: highlight ? '#2FA53F' : '#D0D3DA',
        alignItems: 'center',
        justifyContent: 'center',
        transform: [{ rotate }],
      }}
    >
      {value === null ? (
        <Text style={{ fontSize: 22, color: '#9AA0AC', fontWeight: '700' }}>?</Text>
      ) : (
        <DiePips value={value} />
      )}
    </Animated.View>
  );
}

// Pip layout for 1..6 in a 3×3 grid.
const PIP_LAYOUT: Record<number, ReadonlyArray<number>> = {
  1: [4],
  2: [0, 8],
  3: [0, 4, 8],
  4: [0, 2, 6, 8],
  5: [0, 2, 4, 6, 8],
  6: [0, 2, 3, 5, 6, 8],
};

function DiePips({ value }: { value: number }): JSX.Element {
  const on = new Set(PIP_LAYOUT[value] ?? []);
  return (
    <View style={{ width: 40, height: 40, flexDirection: 'row', flexWrap: 'wrap' }}>
      {Array.from({ length: 9 }).map((_, i) => (
        <View key={i} style={{ width: 40 / 3, height: 40 / 3, alignItems: 'center', justifyContent: 'center' }}>
          {on.has(i) ? (
            <View style={{ width: 8, height: 8, borderRadius: 4, backgroundColor: '#15171C' }} />
          ) : null}
        </View>
      ))}
    </View>
  );
}
